import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { ChartConfiguration, Plugin, TooltipItem } from 'chart.js';
import moment from 'moment';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import {
  getQuestionnaireScoreRanges,
  getQuestionnaireScoreState,
  QuestionnaireScoreBadge,
  QuestionnaireScoreState,
  QuestionnaireScoreRange,
} from 'src/app/utils/questionnaire-history-score';
import { datetime_format } from 'src/date/date.configuration';

@Component({
  selector: 'app-questionnaire-history-chart',
  templateUrl: './questionnaire-history-chart.component.html',
  styleUrls: ['./questionnaire-history-chart.component.css'],
  standalone: false,
})
export class QuestionnaireHistoryChartComponent
  implements OnChanges, AfterViewInit
{
  @Input() data: QuestionnaireHistoryItem[] = [];
  @Input() questionnaireTemplateId?: number;
  @Input() title = '';
  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;

  chart?: Chart;
  private readonly riskBandsPlugin: Plugin = {
    id: 'questionnaireRiskBands',
    beforeDatasetsDraw: (chartInstance, _args, pluginOptions) => {
      const ranges = (pluginOptions as { ranges?: QuestionnaireScoreRange[] })
        ?.ranges;
      const yScale = chartInstance.scales['y'];
      const chartArea = chartInstance.chartArea;

      if (!ranges?.length || !yScale || !chartArea) {
        return;
      }

      const ctx = chartInstance.ctx;
      ctx.save();

      ranges.forEach((range: QuestionnaireScoreRange, index: number) => {
        const nextRange = ranges[index + 1];
        const upperBoundary =
          nextRange?.min ?? range.max ?? yScale.max;
        const lower = Math.max(range.min, yScale.min);
        const upper = Math.min(upperBoundary, yScale.max);

        if (upper <= lower) {
          return;
        }

        const top = yScale.getPixelForValue(upper);
        const bottom = yScale.getPixelForValue(lower);

        ctx.fillStyle = this.getToneBackgroundColor(range.tone);
        ctx.fillRect(
          chartArea.left,
          top,
          chartArea.right - chartArea.left,
          bottom - top,
        );
      });

      ctx.restore();
    },
  };

  constructor(private readonly translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['data'] ||
      changes['questionnaireTemplateId'] ||
      changes['title']
    ) {
      this.updateChart();
    }
  }

  ngAfterViewInit(): void {
    this.updateChart();
  }

  private updateChart(): void {
    if (!this.canvas?.nativeElement) {
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }

    const graphItems = (this.data ?? [])
      .map((item: QuestionnaireHistoryItem) => ({
        item,
        scoreState: getQuestionnaireScoreState(item),
      }))
      .filter(
        (
          entry: {
            item: QuestionnaireHistoryItem;
            scoreState: QuestionnaireScoreState;
          },
        ) =>
          entry.scoreState.kind === 'scored' ||
          entry.scoreState.kind === 'incomplete',
      )
      .sort((left, right) => {
        const leftDate = this.getTimestamp(left.item);
        const rightDate = this.getTimestamp(right.item);
        return leftDate - rightDate;
      });

    if (!graphItems.length) {
      return;
    }

    const scoredItems = graphItems.filter(
      (
        entry: {
          item: QuestionnaireHistoryItem;
          scoreState: QuestionnaireScoreState;
        },
      ): entry is {
        item: QuestionnaireHistoryItem;
        scoreState: Extract<QuestionnaireScoreState, { kind: 'scored' }>;
      } => entry.scoreState.kind === 'scored',
    );

    if (!scoredItems.length) {
      return;
    }

    const ranges =
      scoredItems
        .map((entry) =>
          getQuestionnaireScoreRanges(this.questionnaireTemplateId, entry.item),
        )
        .find((entry) => entry.length > 0) ?? [];
    const chartValues = scoredItems.map((entry) => entry.scoreState.value);
    const plotValues = graphItems.map((entry) =>
      entry.scoreState.kind === 'scored' ? entry.scoreState.value : null,
    );
    const yMax = this.getYMax(chartValues, ranges);

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels: graphItems.map((entry) => this.getLabel(entry.item)),
        datasets: [
          {
            label: this.translate.instant(this.title || 'Questionnaire score'),
            data: plotValues,
            borderColor: '#1677ff',
            backgroundColor: '#1677ff',
            pointBackgroundColor: '#1677ff',
            pointBorderColor: '#1677ff',
            pointHoverBackgroundColor: '#1677ff',
            pointHoverBorderColor: '#1677ff',
            pointRadius: 4,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.1,
            spanGaps: false,
            fill: false,
          },
        ],
      },
      plugins: [this.riskBandsPlugin],
      options: {
        animation: false,
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context: TooltipItem<'line'>) =>
                `${this.translate.instant(this.title || 'Questionnaire score')}: ${context.parsed.y}`,
            },
          },
        } as any,
        scales: {
          x: {
            ticks: {
              display: true,
            },
          },
          y: {
            beginAtZero: true,
            min: 0,
            max: yMax,
            ticks: {
              display: true,
            },
          },
        },
      },
    };

    ((config.options as any).plugins as {
      questionnaireRiskBands?: { ranges: QuestionnaireScoreRange[] };
    }).questionnaireRiskBands = {
      ranges,
    };

    this.chart = new Chart(this.canvas.nativeElement, config);
  }

  private getTimestamp(item: QuestionnaireHistoryItem): number {
    const rawValue =
      item.submittedDate ??
      item.lastUpdateDate ??
      item.createdDate ??
      item.creationDate;

    return rawValue ? new Date(rawValue).getTime() || 0 : 0;
  }

  private getLabel(item: QuestionnaireHistoryItem): string {
    const rawValue =
      item.submittedDate ??
      item.lastUpdateDate ??
      item.createdDate ??
      item.creationDate;

    return rawValue ? moment(rawValue).format(datetime_format) : '';
  }

  private getToneColor(
    tone: QuestionnaireScoreBadge['tone'],
  ): string {
    switch (tone) {
      case 'green':
        return '#52c41a';
      case 'yellow':
        return '#fadb14';
      case 'orange':
        return '#fa8c16';
      case 'red':
        return '#ff4d4f';
      default:
        return '#1677ff';
    }
  }

  private getToneBackgroundColor(
    tone: QuestionnaireScoreBadge['tone'],
  ): string {
    switch (tone) {
      case 'green':
        return 'rgba(82, 196, 26, 0.12)';
      case 'yellow':
        return 'rgba(250, 219, 20, 0.16)';
      case 'orange':
        return 'rgba(250, 140, 22, 0.14)';
      case 'red':
        return 'rgba(255, 77, 79, 0.12)';
      default:
        return 'rgba(22, 119, 255, 0.08)';
    }
  }

  private getYMax(
    values: number[],
    ranges: QuestionnaireScoreRange[],
  ): number {
    const dataMax = Math.max(...values, 0);
    const finiteRangeMax = ranges.reduce(
      (max: number, range: QuestionnaireScoreRange) =>
        range.max !== undefined ? Math.max(max, range.max) : max,
      0,
    );
    const openEndedStarts = ranges
      .filter((range: QuestionnaireScoreRange) => range.max === undefined)
      .map((range: QuestionnaireScoreRange) => range.min);
    const openEndedHeadroom =
      openEndedStarts.length > 0
        ? Math.max(
            ...openEndedStarts.map((start: number) =>
              Math.max(start + 3, Math.ceil(start * 1.25)),
            ),
          )
        : 0;

    const baseline = Math.max(dataMax, finiteRangeMax, openEndedHeadroom, 5);
    return Math.ceil(baseline);
  }
}
